import React, { ImgHTMLAttributes, ReactElement } from 'react';
import clsx from 'clsx';

import {
  addParamsToContentfulImage,
  ContentfulImageFocusTypes,
  ImageParams,
  transformImageParams,
} from '../../helpers/contentful-image-api';
import styles from './ContentfulPicture.module.scss';

type Profile = {
  width: number;
  height: number;
  rule: string;
  focusType?: ContentfulImageFocusTypes;
};

type Position = 'default' | 'absolute';

interface Props {
  url: string;
  alt: string;
  profiles: Profile[];
  lazy?: boolean;
  decoding?: 'sync' | 'async' | 'auto';
  containerClassName?: string;
  className?: string;
  focusType?: ContentfulImageFocusTypes;
  Label?: React.ReactNode;
  position?: Position;
}

const bigToSmall = (ruleA: Profile, ruleB: Profile) => {
  const valueA = parseInt(ruleA.rule.split('px')[0], 10);
  const valueB = parseInt(ruleB.rule.split('px')[0], 10);

  if (!valueA && !valueB) return 0;

  return valueB - valueA;
};

function ContentfulPicture({
  url,
  alt = '',
  profiles,
  lazy = false,
  decoding = 'async',
  focusType,
  containerClassName,
  className,
  Label,
  position = 'default',
}: Props): ReactElement {
  const isSVG = url.split('.').pop()?.toLowerCase() === 'svg';

  const sortedProfiles = profiles.sort(bigToSmall);

  const imageClassNames = clsx(styles.img, className);

  if (isSVG) {
    return (
      <picture className={clsx(styles.picture, containerClassName)}>
        <img alt={alt} className={styles.img} src={url} />
      </picture>
    );
  }

  const imagePath = (params: ImageParams) =>
    addParamsToContentfulImage(url, transformImageParams(params));

  const imagePathRetinas = ({ width, height, ...params }: ImageParams) => {
    const image1x = imagePath({ width, height, ...params });
    const image2x = imagePath({
      ...(width ? { width: width * 2 } : {}),
      ...(height ? { height: height * 2 } : {}),
      ...params,
    });

    return `${image1x} 1x, ${image2x} 2x`;
  };

  const src = imagePath({
    ...sortedProfiles[sortedProfiles.length - 1],
    format: 'jpg',
    focusType,
  });

  const imageProps: ImgHTMLAttributes<HTMLImageElement> = {
    alt,
    src,
    width: sortedProfiles[sortedProfiles.length - 1].width,
    height: sortedProfiles[sortedProfiles.length - 1].height,
    ...(lazy ? { loading: 'lazy', decoding } : {}),
  };

  const srcSets = sortedProfiles.map((profile) => (
    <React.Fragment key={`${url}_${profile.height}_${profile.width}`}>
      <source
        media={`(min-width: ${profile.rule})`}
        srcSet={imagePathRetinas({
          focusType,
          ...profile,
          format: 'avif',
          quality: 80,
        })}
        type="image/avif"
      />
      <source
        media={`(min-width: ${profile.rule})`}
        srcSet={imagePathRetinas({
          focusType,
          ...profile,
          format: 'webp',
          quality: 80,
        })}
        type="image/webp"
      />
      <source
        media={`(min-width: ${profile.rule})`}
        srcSet={imagePathRetinas({
          focusType,
          ...profile,
          format: 'jpg',
          quality: 90,
        })}
        type="image/jpeg"
      />
    </React.Fragment>
  ));

  return (
    <picture
      className={clsx(styles.picture, containerClassName, {
        [styles.absolute]: position === 'absolute',
      })}
    >
      {srcSets}
      {Label && <div className={styles.label}>{Label}</div>}
      <img alt={alt} className={imageClassNames} {...imageProps} />
    </picture>
  );
}

export default ContentfulPicture;
